import styled from '@emotion/styled';
import React from 'react';
import { Image } from '~/shared/components';
import Flex from '~/shared/components/Flex/Flex';
import { useTranslation } from '~/shared/utils/translation';
import { Product, ProductItem$1, PromotionType } from '~/lib/data-contract/salesforce';
import { Details } from '../styled';
import { breakpoints } from '~/theme/breakpoints';
import { mq } from '~/lib';
import useProductPersonalization from '~/services/personalization/useProductPersonalization';
import { useUtils } from '~/shared/hooks/useUtils';

type ComponentProps = Product & ProductItem$1;

export const ImageWrap = styled.div(({ theme }) => ({
    height: '100%',
    width: '120px',
    minWidth: '100px',
    background: theme.colors.productImageBackround,
    position: 'relative',
    img: {
        mixBlendMode: 'multiply',
    },

    [breakpoints.lg]: {
        height: '100%',
        width: '126px',
    },
    [mq(0, 'xs')]: {
        height: '150px',
        width: '100%',
    },
}));

const DetailsWrap = styled.div(() => ({}));

const PriceWrap = styled.div(({ theme }) => ({
    padding: `${theme.space[2]} ${theme.space[3]}`,
    border: `1px solid ${theme.colors.grey35}`,
    borderRadius: theme.sizes.cornerRadius,
    fontSize: theme.fontSizes['exs'],
    textAlign: 'center',

    [breakpoints.xs]: {
        border: 'none',
        padding: 0,
        alignSelf: 'baseline',
    },
}));

const Brand = styled.div(({ theme }) => ({
    ...theme.mixins.useTextStyle('formsLabelActive'),
    fontWeight: theme.fontWeights.regular,
    fontSize: theme.fontSizes['exs'],
    [breakpoints.md]: {
        fontSize: theme.fontSizes['2xs'],
    },
    [breakpoints.lg]: {
        fontSize: theme.fontSizes['xs'],
    },
}));

const Name = styled.div(({ theme }) => ({
    fontSize: theme.fontSizes['2xs'],
    lineHeight: '14px',
    padding: '8px 0',
    fontWeight: theme.fontWeights.medium,
    [breakpoints.md]: {
        fontSize: theme.fontSizes['sm'],
    },
    [breakpoints.lg]: {
        fontSize: theme.fontSizes['md'],
    },
}));

const Synopsis = styled.div(({ theme }) => ({
    fontSize: theme.fontSizes['exs'],
    lineHeight: '14px',
    fontWeight: theme.fontWeights.light,
    [breakpoints.md]: {
        fontSize: theme.fontSizes['2xs'],
    },
    [breakpoints.lg]: {
        fontSize: theme.fontSizes['xs'],
    },
}));

const Description = styled.div(({ theme }) => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100px',
    ...theme.mixins.useTextStyle('formsLabelActive'),
}));

const Content = styled(Flex)(({ theme }) => ({
    flex: 1,
    padding: `${theme.space[2]} ${theme.space[2]} 0 ${theme.space[2]}`,

    [breakpoints.md]: {
        padding: theme.space[2],
    },
    [breakpoints.lg]: {
        padding: theme.space[3],
    },
}));

const Prices = styled(Flex)(({ theme }) => ({
    flex: 1,
    flexDirection: 'row',
    lineHeight: theme.lineHeights.base,
    gap: '10px',
    alignItems: 'center',
    justifyContent: 'end',
    [breakpoints.xs]: {
        gap: 0,
        flexDirection: 'column',
        width: '100%',
        justifyContent: 'flex-start',
        alignItems: 'baseline',
        padding: `0 ${theme.space[2]} ${theme.space[2]} ${theme.space[2]}`,
    },
}));

const Personalization = styled.div(({ theme }) => ({
    color: theme.colors.black,
    fontSize: theme.fontSizes['exs'],
    lineHeight: theme.lineHeights.compact,
    marginTop: '10px',
    marginBottom: '10px',
    [breakpoints.md]: {
        fontSize: theme.fontSizes['2xs'],
    },
    [breakpoints.lg]: {
        fontSize: theme.fontSizes['xs'],
    },

    em: {
        fontStyle: 'italic',
    },
}));

const PriceLabel = styled.div<{ beforePrice?: boolean }>(({ theme, beforePrice }) => ({
    color: beforePrice ? theme.colors.grey50 : theme.colors.black,
    textDecoration: beforePrice ? 'line-through' : 'none',
    fontSize: theme.fontSizes['exs'],
    [breakpoints.md]: {
        fontSize: theme.fontSizes['2xs'],
    },
    [breakpoints.lg]: {
        fontSize: theme.fontSizes['xs'],
    },
}));

export const ProductCard = (props: ComponentProps) => {
    const {
        brand,
        name,
        price,
        pricePerUnit,
        quantity,
        description,
        image,
        large,
        priceAfterOrderDiscount,
        c_synopsis,
    } = props;
    const { translate } = useTranslation();
    const { convertPriceToLocale } = useUtils();
    const {
        personalizationMetadataEntries,
        personalizationTitleWithPrice,
        shouldAllowPersonalization,
        addPersonalizationCostToPrice,
    } = useProductPersonalization(props);

    const shouldShowPersonalizationDetails =
        shouldAllowPersonalization && !!personalizationMetadataEntries;

    const quantityValue = Number(quantity) || 1;
    const pricePerUnitAfterDiscount = Number(priceAfterOrderDiscount) / quantityValue;
    const priceLabel = `${quantity} ${translate('checkout.summery.product-quantity')}`;

    const isDiscounted = price !== priceAfterOrderDiscount;
    const isChristmasPrice = props.c_activepromo?.type === PromotionType.CHRISTMAS_PRICE;
    const isSuperPrice = props.c_activepromo?.type === PromotionType.SUPER_PRICE;
    const isImercoprice = props.c_activepromo?.type === PromotionType.IMERCO_PRICE;

    const christmasBeforePrice = props.c_promotionType?.unitPrice;
    const beforePricePerUnit =
        isChristmasPrice && christmasBeforePrice != null ? christmasBeforePrice : pricePerUnit;

    const shouldShowBeforePrice =
        isDiscounted &&
        !(isSuperPrice || isImercoprice) &&
        beforePricePerUnit != null &&
        Number(beforePricePerUnit) !== Number(pricePerUnitAfterDiscount);

    return (
        <Flex>
            <ImageWrap>
                {image && (
                    <Image
                        objectFit="cover"
                        width={large ? 96 : 72}
                        height={large ? 96 : 72}
                        sizes="(min-width: 1440px) 126px, 120px"
                        src={image.link}
                        alt={image.alt}
                    />
                )}
            </ImageWrap>

            <Details alignItems="baseline" justifyContent="space-between">
                <Content flex justifyContent="space-between">
                    <DetailsWrap>
                        <Brand>{brand}</Brand>
                        <Name>{name}</Name>
                        <Synopsis>{c_synopsis}</Synopsis>
                        <Description>{description}</Description>

                        {shouldShowPersonalizationDetails && (
                            <Personalization>
                                {personalizationTitleWithPrice && (
                                    <p>
                                        <strong>{personalizationTitleWithPrice}</strong>
                                    </p>
                                )}

                                {personalizationMetadataEntries.map(({ key, value }) => (
                                    <p key={key}>
                                        {key}: <em>{value}</em>
                                    </p>
                                ))}
                            </Personalization>
                        )}
                    </DetailsWrap>
                </Content>
                <Prices>
                    {priceAfterOrderDiscount && (
                        <>
                            <PriceLabel>
                                {addPersonalizationCostToPrice(pricePerUnitAfterDiscount)}
                                <span> {translate('basket.perItem')}</span>
                            </PriceLabel>

                            {shouldShowBeforePrice && (
                                <PriceLabel beforePrice>
                                    {isChristmasPrice
                                        ? convertPriceToLocale(beforePricePerUnit)
                                        : beforePricePerUnit}
                                </PriceLabel>
                            )}
                        </>
                    )}
                    <PriceWrap>{priceLabel}</PriceWrap>
                </Prices>
            </Details>
        </Flex>
    );
};
